import Decimal from 'decimal.js';
import { serviceModel, serviceCostModel, serviceBillingModel } from "../../models";
export let BillingGroupTypes = /*#__PURE__*/function (BillingGroupTypes) {
  BillingGroupTypes["Today"] = "today";
  BillingGroupTypes["OnEffectiveDate"] = "on the effective date";
  BillingGroupTypes["InStages"] = "in stages";
  BillingGroupTypes["Once"] = "once";
  BillingGroupTypes["Weekly"] = "weekly";
  BillingGroupTypes["BiWeekly"] = "every two weeks";
  BillingGroupTypes["Monthly"] = "monthly";
  BillingGroupTypes["Quarterly"] = "quarterly";
  BillingGroupTypes["Yearly"] = "yearly";
  return BillingGroupTypes;
}({});
export function summarize() {
  let {
    selectedOptionalServices = [],
    effectiveDate = null,
    now = new Date(),
    billingFrequencyOnly = false,
    START_BILLING_AT = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    selectedOptionalServices: [],
    effectiveDate: null,
    now: new Date(),
    billingFrequencyOnly: false,
    START_BILLING_AT: false
  };
  return function doSummary(summary, service) {
    const cost = serviceModel.cost(service);
    const variablePrice = serviceCostModel.isVariablePrice(cost);
    const rangePrice = serviceCostModel.isRangePrice(cost);
    const name = serviceModel.name(service);
    const billedUpfront = serviceModel.isBilledUpfront(service);
    const billing = serviceModel.billingTrigger(service);
    const period = serviceBillingModel.period(billing);
    const recurring = serviceBillingModel.isRecurring(billing);
    const automatic = serviceBillingModel.isAutomatic(billing);
    const price = serviceModel.applyDiscount(service);
    const minPrice = serviceModel.minPriceFinal(service);
    const prorated = serviceModel.isProrated(service);
    const paused = serviceModel.isPaused(service);
    const skipsOnAcceptance = serviceModel.isSkipBillingOnAcceptance(service);
    const optional = serviceModel.isOptional(service.participation) && !selectedOptionalServices.includes(serviceModel.id(service));
    const unitName = serviceModel.unitName(service);
    const isFutureDate = effectiveDate != null && effectiveDate > now;
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(billing);
    // when START_BILLING_AT is enabled, never use OnEffectiveDate - always use Today for immediate billing
    const todayGroup = START_BILLING_AT ? BillingGroupTypes.Today : isFutureDate ? BillingGroupTypes.OnEffectiveDate : BillingGroupTypes.Today;
    if (optional) return summary;

    // exclude manual services from billing schedule, except for services billed in stages
    if (!automatic && !oneTimeInStages && START_BILLING_AT) return summary;
    if (recurring) {
      const startBillingAt = serviceModel.startBillingAt(service);
      const effectiveBillingDate = START_BILLING_AT ? startBillingAt || (isFutureDate ? effectiveDate : null) : null;
      const serviceStartsInFuture = START_BILLING_AT && effectiveBillingDate && effectiveBillingDate > now;
      if (automatic && billedUpfront && !paused && !skipsOnAcceptance && !serviceStartsInFuture) {
        addServiceToSummary(todayGroup);
      }
    } else {
      if (oneTimeInStages) {
        oneTimeInStages.stagesWithFinalAmount(cost).forEach((stage, index) => {
          const isFirstStageAutomatic = index === 0 && oneTimeInStages.isAutoFirst();
          const shouldNotBillToday = START_BILLING_AT && isFutureDate;
          const shouldBeInToday = isFirstStageAutomatic && !billingFrequencyOnly && !shouldNotBillToday;
          const groupType = shouldBeInToday ? todayGroup : BillingGroupTypes.InStages;
          addBillingStageToSummary(groupType, oneTimeInStages, stage, cost);
        });
      } else if (automatic && !billingFrequencyOnly) {
        addServiceToSummary(todayGroup);
      } else {
        addServiceToSummary(BillingGroupTypes.Once);
      }
    }
    if (period != null) {
      const group = (() => {
        if (period === 'weekly') {
          return BillingGroupTypes.Weekly;
        } else if (period === 'biweekly') {
          return BillingGroupTypes.BiWeekly;
        } else if (period === 'monthly') {
          return BillingGroupTypes.Monthly;
        } else if (period === 'quarterly') {
          return BillingGroupTypes.Quarterly;
        } else {
          return BillingGroupTypes.Yearly;
        }
      })();
      addServiceToSummary(group);
    }
    return summary;
    function addBillingStageToSummary(groupType, oneTimeInStages, billingStage, cost) {
      const group = getServiceSummary(groupType);
      group.totalAmount = group.totalAmount.plus(billingStage.discountedFinalAmount);
      group.services.push({
        name,
        price: billingStage.discountedFinalAmount,
        discount: cost.discount,
        minPrice: null,
        prorated: false,
        isRange: false,
        unit: null,
        billingStages: {
          number: billingStage.stageNumber,
          total: oneTimeInStages.numStages()
        }
      });
    }
    function addServiceToSummary(groupType) {
      const group = getServiceSummary(groupType);
      const startBillingAt = serviceModel.startBillingAt(service);
      group.totalAmount = group.totalAmount.plus(price);
      group.services = group.services.concat({
        name,
        price,
        minPrice,
        prorated,
        isRange: rangePrice,
        unit: unitName,
        discount: cost.discount
      });
      group.hasBilledUpfront = group.hasBilledUpfront || billedUpfront || false;
      if (START_BILLING_AT) {
        // Use service's own startBillingAt, or relationship effective date if service is automatic and doesn't skip billing on acceptance
        const effectiveBillingDate = startBillingAt || (automatic && !skipsOnAcceptance && isFutureDate ? effectiveDate : null);
        if (effectiveBillingDate) {
          group.startDate = effectiveBillingDate;
        }
      }
      return summary;
    }
    function getServiceSummary(groupType) {
      if (summary[groupType] == null) summary[groupType] = new Map();
      if (!summary[groupType].has(variablePrice || rangePrice)) summary[groupType].set(variablePrice || rangePrice, {
        totalAmount: new Decimal(0),
        services: [],
        hasBilledUpfront: false,
        startDate: undefined
      });
      return summary[groupType].get(variablePrice || rangePrice);
    }
  };
}